import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/photos/hero-notte.jpg";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.55 + i * 0.14, duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative h-[100svh] min-h-[640px] overflow-hidden flex flex-col justify-end"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Pescatore in barca su un lago buio"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-ink/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />

      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ delay: 0.35, duration: 0.7 }}
        className="absolute inset-0 bg-ink z-30 pointer-events-none"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 px-5 md:px-10 pb-8 md:pb-10 pt-32"
      >
        <motion.p
          initial="hidden"
          animate="show"
          custom={0}
          variants={line}
          className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-ember mb-5 md:mb-7"
          data-testid="hero-eyebrow"
        >
          Progetto editoriale indipendente di carpfishing — N.01
        </motion.p>

        <h1 className="font-serif font-light leading-[0.86] tracking-[-0.02em]">
          <span className="block overflow-hidden">
            <motion.span
              data-testid="hero-title"
              initial="hidden"
              animate="show"
              custom={1}
              variants={line}
              className="block text-[clamp(4.5rem,17vw,13rem)]"
            >
              STILL
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              initial="hidden"
              animate="show"
              custom={2}
              variants={line}
              className="block outline-text text-[clamp(4.5rem,17vw,13rem)]"
            >
              WATER
            </motion.span>
          </span>
        </h1>

        <div className="mt-6 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <span className="block overflow-hidden">
            <motion.p
              initial="hidden"
              animate="show"
              custom={3}
              variants={line}
              className="font-serif italic text-xl md:text-3xl text-ember"
            >
              il giornale delle acque ferme
            </motion.p>
          </span>
          <span className="block overflow-hidden max-w-xl">
            <motion.p
              initial="hidden"
              animate="show"
              custom={4}
              variants={line}
              className="text-sm md:text-base text-mist leading-relaxed max-w-xl"
            >
              Rubriche video sul carpfishing: novità, interviste dal settore,
              sessioni in riva. Un diario che esce quando l'acqua è giusta.
            </motion.p>
          </span>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          custom={5}
          variants={line}
          className="mt-7 md:mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-rubriche"
            onClick={() => scrollToId("rubriche")}
            className="bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-3.5 rounded-full hover:bg-ember-bright transition-colors duration-300"
          >
            Scopri le rubriche
          </button>
          <button
            data-testid="hero-cta-diario"
            onClick={() => scrollToId("diario")}
            className="border border-bone/30 text-bone font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-3.5 rounded-full hover:border-ember hover:text-ember transition-colors duration-300"
          >
            Il Diario delle Acque
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 border-t border-bone/15"
      >
        <div className="px-5 md:px-10 py-4 flex items-center justify-between font-mono text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-faint">
          <span>Acqua dolce — Italia</span>
          <span className="hidden md:inline">Rubriche video, cinque fili</span>
          <span className="flex items-center gap-3">
            Scorri
            <span className="block w-px h-8 bg-ember scroll-cue-line" />
          </span>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="hidden md:block absolute right-8 top-1/2 -translate-y-1/2 z-10 font-mono text-[10px] uppercase tracking-[0.4em] text-bone/50 [writing-mode:vertical-rl]"
      >
        Senza sponsor — senza filtri
      </motion.p>
    </section>
  );
}
